/**
 * @fileoverview Manages configuration settings for the application, providing a singleton-based settings provider that can read, write, and retrieve API keys and other configuration values. Includes a setup function to interactively configure settings and handle .awignore and .gitignore file modifications.
 */

import * as fs from 'fs'
import * as path from 'path'
import { input } from '@inquirer/prompts'
import { info, error } from '@iterator/shared-node/util/logging'
import { getAwolfPaths, readJsonFile, writeJsonFile } from './awolf_dir'
import { ApiKeys, PROVIDER_API_KEYS, KeyProvider } from '@iterator/shared-node/ai/api_keys'

interface Settings {
  [key: string]: any
}

const ALLOWED_SETTINGS = PROVIDER_API_KEYS
export type AllowedSetting = ApiKeys

export class AwolfSettings implements KeyProvider {
  settingsPath: string
  private settings: Settings

  private static Singleton: AwolfSettings | null = null

  public static singleton(): AwolfSettings {
    if (!AwolfSettings.Singleton) {
      AwolfSettings.Singleton = new AwolfSettings()
    }
    return AwolfSettings.Singleton
  }

  constructor(settingsPath: string = getAwolfPaths(process.env.HOME || '').settings) {
    this.settingsPath = settingsPath
    this.settings = readJsonFile<Settings>(this.settingsPath)
  }

  get(key: AllowedSetting): any {
    return this.settings[key] || process.env[key]
  }

  set(key: AllowedSetting, value: any): void {
    if (!ALLOWED_SETTINGS.includes(key)) {
      throw new Error(`Attempted to set an invalid setting: ${key}`)
    }
    this.settings[key] = value
    writeJsonFile(this.settingsPath, this.settings)
  }

  /**
   * @returns the settings that are not set
   */
  checkRequiredSettings(settings: AllowedSetting[]): AllowedSetting[] {
    return settings.filter((setting) => !this.get(setting))
  }
}

export function requireSettings(settings: ApiKeys[]) {
  const missing = AwolfSettings.singleton().checkRequiredSettings(settings)
  if (missing.length > 0) {
    throw new Error(`Settings are required: ${missing.join(', ')}`)
  }
}

export async function setupSettings() {
  const settings = new AwolfSettings()

  for (const setting of ALLOWED_SETTINGS) {
    const answer = await input({
      message: `${setting}:`,
      default: settings.get(setting) || '',
    })
    settings.set(setting, answer)
  }
  info(`Settings saved to ${settings.settingsPath}`)

  const awignorePath = path.join(process.cwd(), '.awignore')
  if (!fs.existsSync(awignorePath)) {
    try {
      fs.writeFileSync(awignorePath, '.awolf\n', 'utf8')
      info('Created .awignore file')
    } catch (e) {
      error('Failed to create .awignore file:', e)
    }
  }

  const gitignorePath = path.join(process.cwd(), '.gitignore')
  if (fs.existsSync(gitignorePath)) {
    try {
      const gitignore = fs.readFileSync(gitignorePath, 'utf8')
      if (!gitignore.includes('.awolf')) {
        fs.appendFileSync(gitignorePath, '\n.awolf\n', 'utf8')
        info('Added .awolf to .gitignore')
      }
    } catch (e) {
      error('Failed to update .gitignore:', e)
    }
  }

  info('✅ Set up complete.')
}
